import { useCallback, useEffect, useState } from "react";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";

export interface AiMonthlyNarrative {
  headline: string;
  body: string;
  pattern: string;
  suggestion: string;
}

export type MonthlyNarrativeStatus =
  /** Request in flight. */
  | "loading"
  /** A narrative came back. */
  | "ready"
  /** Backend answered, but had nothing to summarize for this month. */
  | "empty"
  /** Backend answered `status: "insufficient_data"` — too little written to draw a pattern. */
  | "insufficient_data"
  /** Non-OK response, `status: "unavailable"`, or a network failure. */
  | "error";

/** Contract: GET /journals/insights/monthly?monthStart=YYYY-MM-01 */
interface MonthlyInsightResponse {
  narrative: AiMonthlyNarrative | null;
  status?: "ok" | "insufficient_data" | "unavailable";
  monthStart?: string;
  entryCount?: number;
  /** How many unfoldings the month needs before a narrative is generated. */
  minimumEntries?: number;
}

/**
 * Monthly narrative from the backend — `GET /journals/insights/monthly?monthStart=YYYY-MM-01`.
 *
 * Reports a real status rather than collapsing every outcome to `null`: the month
 * is user-selected, so "loading", "not enough written yet" and "request failed"
 * have to be distinguishable on screen. The card never invents prose to fill a gap.
 */
export function useAiMonthlyNarrative(
  monthStart: string,
  options?: { enabled?: boolean },
): {
  narrative: AiMonthlyNarrative | null;
  status: MonthlyNarrativeStatus;
  /** From the backend, for the insufficient-data copy. Null when not supplied. */
  minimumEntries: number | null;
  entryCount: number | null;
  retry: () => void;
} {
  const enabled = options?.enabled ?? true;
  const [narrative, setNarrative] = useState<AiMonthlyNarrative | null>(null);
  const [status, setStatus] = useState<MonthlyNarrativeStatus>("loading");
  const [minimumEntries, setMinimumEntries] = useState<number | null>(null);
  const [entryCount, setEntryCount] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setNarrative(null);
      setMinimumEntries(null);
      setEntryCount(null);
      setStatus("empty");
      return;
    }

    let cancelled = false;
    // Clear prose immediately so a newly picked month never shows the previous
    // month's narrative underneath a fresh request.
    setNarrative(null);
    setStatus("loading");

    authFetch(apiUrl(`/journals/insights/monthly?monthStart=${monthStart}`))
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setStatus("error");
          return;
        }
        const data = (await res.json()) as MonthlyInsightResponse | null;
        if (cancelled) return;

        setMinimumEntries(typeof data?.minimumEntries === "number" ? data.minimumEntries : null);
        setEntryCount(typeof data?.entryCount === "number" ? data.entryCount : null);

        // Branch on the backend's own status; fall back to the shape of the
        // payload for any deploy that predates the status field.
        if (data?.status === "insufficient_data") {
          setStatus("insufficient_data");
          return;
        }
        if (data?.status === "unavailable") {
          setStatus("error");
          return;
        }
        if (data?.narrative) {
          setNarrative(data.narrative);
          setStatus("ready");
          return;
        }
        setStatus("empty");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [monthStart, enabled, attempt]);

  return { narrative, status, minimumEntries, entryCount, retry };
}
